export { formatApiDateTime, parseApiDateTime } from './datetime';
export { canEditStockLocations, roleOf } from './permissions';
export { socioNum, socioNumDelClub } from './socioNum';
export { busquedaSuficiente, minimoBusqueda, filtroBusqueda } from './busquedaSocio';

export function getOrderStatus(status) {
	switch (status) {
		case 'PLACED':
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-sky-600 bg-sky-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
		case 'CONFIRMED':
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-orange-600 bg-orange-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
		case 'SHIPPED':
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-teal-600 bg-teal-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
		case 'OUT_FOR_DELIVERY':
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-yellow-600 bg-yellow-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
		case 'DELIVERED':
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-green-600 bg-green-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
		default:
			return (
				<span className="capitalize py-1 px-2 rounded-md text-xs text-gray-600 bg-gray-100">
					{status.replaceAll('_', ' ').toLowerCase()}
				</span>
			)
	}
}

const imageVersionMap = new Map();

function isExternalUrl(path) {
	return path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:') || path.startsWith('blob:');
}

function buildAbsoluteUrl(path) {
	const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
	if (!apiUrl) {
		return path.startsWith('/') ? path : `/${path}`;
	}

	return `${apiUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
}

function normalizeImagePath(path) {
	if (!path) return null;
	if (isExternalUrl(path)) return path;

	const cleanPath = path.trim();
	if (!cleanPath) return null;

	if (cleanPath.startsWith('/static/') || cleanPath.startsWith('static/')) {
		return buildAbsoluteUrl(cleanPath.replace(/^\//, ''));
	}

	if (cleanPath.startsWith('/media/') || cleanPath.startsWith('media/')) {
		return buildAbsoluteUrl(cleanPath.replace(/^\//, ''));
	}

	if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
		return buildAbsoluteUrl(cleanPath.replace(/^\//, ''));
	}

	if (cleanPath.startsWith('/')) {
		return buildAbsoluteUrl(cleanPath);
	}

	if (cleanPath.includes('/')) {
		return buildAbsoluteUrl(cleanPath);
	}

	return buildAbsoluteUrl(`static/${cleanPath}`);
}

function appendCacheParam(url, cacheValue) {
	if (!url || !cacheValue) return url;
	if (url.startsWith('data:') || url.startsWith('blob:')) return url;
	if (url.includes('X-Amz-Signature')) return url;

	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}v=${encodeURIComponent(cacheValue)}`;
}

function resolveImageArgs(fallbackOrOptions, maybeOptions) {
	if (fallbackOrOptions && typeof fallbackOrOptions === 'object' && !Array.isArray(fallbackOrOptions)) {
		return {
			fallback: fallbackOrOptions.fallback ?? null,
			options: fallbackOrOptions,
		};
	}

	return {
		fallback: fallbackOrOptions ?? null,
		options: maybeOptions ?? {},
	};
}

/**
 * Get the full URL for an image.
 * If the image path is relative, it prepends the API URL.
 * @param {string} path - The image path from the database
 * @param {string} fallback - Fallback image if path is missing
 * @returns {string} - The full URL
 */
export function getImageUrl(path, fallbackOrOptions = null, maybeOptions = {}) {
	const { fallback, options } = resolveImageArgs(fallbackOrOptions, maybeOptions);
	if (!path) return fallback;

	const normalizedUrl = normalizeImagePath(path);
	if (!normalizedUrl) return fallback;

	const cacheValue = options.cacheValue ?? (options.cacheKey ? imageVersionMap.get(options.cacheKey) : null);
	return appendCacheParam(normalizedUrl, cacheValue);
}

export function invalidateImageCache(cacheKey, cacheValue = Date.now()) {
	if (!cacheKey) return cacheValue;
	imageVersionMap.set(cacheKey, cacheValue);
	return cacheValue;
}

export function getImageCacheKey(entityType, entityId) {
	if (!entityType || entityId == null) return null;
	return `${entityType}:${entityId}`;
}
